/**
 * Default Admissions content — mirrors fallbacks in AdmissionsPage.js
 * Used by CMS forms so fields are never empty on first load.
 */
export const ADMISSIONS_DEFAULTS = {
  title: 'Admissions',
  summary: 'Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community.',
  hero_slides: [],

  admissions_levels: [
    {
      title: 'Preschool & Kindergarten Admissions',
      note: 'Trial class available',
      description: 'A gentle entry pathway focused on readiness, social-emotional growth, and a positive first school experience.',
      image: '',
      ageRange: 'Ages 3-6',
      intake: 'Rolling intake with priority Jan - Jul',
      assessment: 'Play-based observation, readiness milestones, and family conversation.',
      support: 'Parent orientation, gentle transition plan, and teacher check-ins in the first term.',
      highlights: [
        'Readiness observation and play-based assessment',
        'Parent-child interaction session',
        'Trial class to experience our learning environment',
        'Flexible intake throughout the year',
      ],
      process: 'Inquiry → Trial Class → Observation → Acceptance',
      outcomes: [
        'Comfortable classroom transition with strong social-emotional foundation',
        'Clear readiness feedback for families before school begins',
        'Personalized onboarding for early learners',
      ],
      timeline: '2-3 weeks',
    },
    {
      title: 'Primary Admissions',
      note: 'Grades 1-6',
      description: 'Placement based on age, readiness, and academic fit with supportive guidance for families new to the school.',
      image: '',
      ageRange: 'Ages 7-12',
      intake: 'Main intake Jan - Jun, limited mid-year slots',
      assessment: 'English, Math, reasoning checks with teacher observation.',
      support: 'Placement guidance, learning pathway briefing, and progress roadmap.',
      highlights: [
        'Academic assessment in English, Math, and reasoning',
        'Teacher observation and learning style evaluation',
        'Student interview to understand interests and goals',
        'Placement in appropriate grade level',
      ],
      process: 'Inquiry → Assessment → Interview → Placement → Acceptance',
      outcomes: [
        'Grade placement aligned to readiness and learning style',
        'Clear academic expectations and support plan for the term',
        'Smooth onboarding for students transferring schools',
      ],
      timeline: '3-4 weeks',
    },
    {
      title: 'Secondary Admissions',
      note: 'Grades 7-12',
      description: 'Academic review and student interview to ensure alignment with learning goals, leadership, and character development.',
      image: '',
      ageRange: 'Ages 13-18',
      intake: 'Main intake Jan - May, limited seats by track',
      assessment: 'Academic diagnostics, goal interview, and transcript review.',
      support: 'Mentoring alignment, subject mapping, and leadership pathway briefing.',
      highlights: [
        'Comprehensive academic assessment and aptitude testing',
        'In-depth student interview on aspirations and values',
        'Review of academic transcript and recommendations',
        'Leadership and character fit evaluation',
      ],
      process: 'Inquiry → Assessment → Interview → Review → Acceptance',
      outcomes: [
        'Learning plan aligned with subject strength and university goals',
        'Leadership pathway recommendations based on interests',
        'Clear expectations for academic milestones',
      ],
      timeline: '4-5 weeks',
    },
  ],

  key_dates: [
    { title: 'Applications Open', tag: 'Jan', date: 'Jan 15, 2026', description: 'Online applications open for the new intake year.' },
    { title: 'Early Bird Window', tag: 'Feb', date: 'Jan 15 - Feb 28, 2026', description: 'Priority scheduling for campus visits and assessment slots.' },
    { title: 'Assessment Week', tag: 'May', date: 'May 10 - May 24, 2026', description: 'Placement tests, classroom observations, or interviews.' },
    { title: 'Offer Release', tag: 'Jun', date: 'Jun 1 - Jun 7, 2026', description: 'Admission results, payment instructions, and enrollment steps sent to families.' },
    { title: 'Orientation Week', tag: 'Jul', date: 'Jul 6 - Jul 10, 2026', description: 'Family briefings, classroom introductions, and readiness activities.' },
    { title: 'School Opening Day', tag: 'Jul', date: 'Jul 13, 2026', description: 'Welcome day for students and families as the new academic year begins.' },
  ],

  fees_payment: [
    {
      title: 'Registration & Admission Fees',
      icon: 'fa-clipboard-list',
      summary: 'Clear onboarding fees so families know what to expect before enrollment.',
      description: 'One-time fees charged upon admission acceptance to secure your child\'s placement and complete enrollment.',
      items: [
        'Application and assessment fee (non-refundable)',
        'One-time registration fee upon acceptance',
        'Uniform and student kit (if applicable)',
        'Enrollment confirmation and placement security',
        'Access to student portal and parent dashboard',
      ],
      highlight: 'Transparent pricing with no hidden charges',
    },
    {
      title: 'Tuition & Payment Plans',
      icon: 'fa-credit-card',
      summary: 'Flexible payment options built around family needs and level pathways.',
      description: 'Annual tuition with multiple payment arrangements to support your family\'s financial planning.',
      items: [
        'Annual tuition based on level (Preschool, Primary, Secondary)',
        'Flexible installment options: monthly, per term, or semester',
        'Sibling discount or multi-child package (where applicable)',
        'Payment methods: bank transfer, virtual account, or on-site cashier',
        'Automatic payment reminders and online payment tracking',
        'Receipts and detailed billing via admissions portal',
      ],
      highlight: 'Installment plans available to ease financial burden',
    },
    {
      title: 'Scholarships & Financial Support',
      icon: 'fa-medal',
      summary: 'Limited assistance for qualifying students with transparent review steps.',
      description: 'Merit-based and needs-based scholarships available for eligible families.',
      items: [
        'Merit-based scholarships for academic excellence',
        'Needs-based financial assistance (subject to availability)',
        'Transparent documentation and review process',
        'Application deadline and eligibility criteria provided',
        'Scholarship review conducted by admissions committee',
        'Contact admissions team for personalized guidance',
      ],
      highlight: 'Supporting talented students from all backgrounds',
    },
    {
      title: 'Additional Services & Programs',
      icon: 'fa-star',
      summary: 'Optional services that enhance your child\'s school experience.',
      description: 'Enrichment programs and support services available to complement your child\'s learning journey.',
      items: [
        'Transportation and shuttle service (optional, monthly fee)',
        'Lunch program with nutritious meal plans (optional)',
        'After-school care and enrichment clubs',
        'School trips, excursions, and special events',
        'Extended learning programs and tutoring support',
        'Technology and digital learning resources',
      ],
      highlight: 'Flexible add-ons to support your child\'s growth',
    },
  ],

  online_application: {
    title: 'Online Application Form',
    summary: 'Ready to enroll? Start your application online in just a few minutes — our admissions team will guide you at every step.',
    ctaLabel: 'Apply Online Now',
    ctaHref: '#register',
    note: 'Have questions? Contact admissions anytime. Already have an account? Log in to continue your application and upload documents.',
    image: '',
    highlights: ['10-minute application', 'Secure document upload', 'Real-time status tracking'],
  },
};

const ARRAY_FIELDS = ['hero_slides', 'admissions_levels', 'key_dates', 'fees_payment'];
const OBJECT_FIELDS = ['online_application'];

export function mergeAdmissionsData(dbData = {}) {
  const merged = { ...ADMISSIONS_DEFAULTS, ...dbData };

  ARRAY_FIELDS.forEach((field) => {
    if (!Array.isArray(dbData[field]) || dbData[field].length === 0) {
      merged[field] = ADMISSIONS_DEFAULTS[field];
    }
  });

  OBJECT_FIELDS.forEach((field) => {
    if (!dbData[field] || typeof dbData[field] !== 'object') {
      merged[field] = ADMISSIONS_DEFAULTS[field];
    } else {
      merged[field] = { ...ADMISSIONS_DEFAULTS[field], ...dbData[field] };
    }
  });

  return merged;
}
